import { useCallback } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { API_URL, startXSignIn } from "./api";
import { qk } from "./queries";
import { setShellNotice } from "./shell";

/**
 * Starting a session with X — the other half of this file, which is otherwise
 * about ending one.
 *
 * It lives here because THREE screens offer the same exit to the provider now:
 * the sign-in screen, the identity card at the top of onboarding, and the
 * profile room's "get a door of your own". They were two copies of the same
 * try/catch and would have become three. Resolves with the sentence to put on
 * screen, or `null` when the browser is already on its way out of the app.
 */
export async function continueWithX(as?: string): Promise<string | null> {
  setShellNotice(null);
  try {
    window.location.href = await startXSignIn(as);
    return null;
  } catch (cause) {
    // Swallowing this made a browser-only failure impossible to diagnose: a
    // blocked request, a stale tab pointing at a dead port and a real provider
    // outage all produced the same sentence. Say which, and leave the
    // underlying error where a developer will actually find it.
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    const reachable = cause instanceof Error && !/fetch|network|load failed/i.test(cause.message);
    console.error("[nob] X sign-in failed", cause);
    return offline
      ? "You are offline."
      : reachable
        ? "X turned that down. Use the email link instead."
        : `Nob is not answering at ${API_URL}. Is the api running?`;
  }
}

/**
 * The session is gone, said once.
 *
 * Two things have to happen together and in this order: the cached identity
 * becomes `null` so every screen agrees who is signed in, and the browser goes
 * home — where `null` renders the sign-in screen, carrying the
 * sentence that explains why it is looking at one.
 */
export function expireSession(queryClient: QueryClient, goHome: () => void): void {
  queryClient.setQueryData(qk.me(), null);
  setShellNotice("That session expired. Sign in again.");
  goHome();
}

/**
 * The same, for the writes — a knock that meets a 401, a rules save that does.
 * Reads go through the query client's own handler; a mutation is answered by
 * the screen that made it, which is why this is a hook and not a subscription.
 */
export function useSessionExpired(): () => void {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useCallback(
    () => expireSession(queryClient, () => void navigate({ to: "/" })),
    [queryClient, navigate],
  );
}
